import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const SubscriptionSuccess = ({ auth }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/status/${sessionId}`, {
        withCredentials: true,
        headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
      });

      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        // Refresh user data
        auth.checkAuth();
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-0 shadow-xl">
        <CardContent className="p-8 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 text-maple mx-auto mb-4 animate-spin" />
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Processing Payment
              </h2>
              <p className="text-slate-600">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Payment Successful!
              </h2>
              <p className="text-slate-600 mb-6">
                Thank you for upgrading! Your subscription is now active.
              </p>
              {paymentData && paymentData.amount && (
                <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left space-y-2">
                  <p className="text-sm text-slate-600">
                    Plan: <span className="font-medium text-slate-900 capitalize">
                      {paymentData.tier || 'Pro'}
                    </span>
                  </p>
                  <p className="text-sm text-slate-600">
                    Amount: <span className="font-medium text-slate-900">
                      ${paymentData.amount} {paymentData.currency}
                    </span>
                  </p>
                </div>
              )}
              <Button 
                onClick={() => navigate("/dashboard")}
                className="bg-maple hover:bg-maple-600 text-white w-full h-12"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {(status === "error" || status === "expired") && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                {status === "expired" ? "Session Expired" : "Payment Failed"}
              </h2>
              <p className="text-slate-600 mb-6">
                {status === "expired" 
                  ? "Your payment session has expired. Please try again."
                  : "We couldn't process your payment. Please try again."}
              </p>
              <Button 
                onClick={() => navigate("/pricing")}
                className="bg-maple hover:bg-maple-600 text-white w-full h-12"
              >
                Try Again
              </Button>
            </>
          )}

          {status === "timeout" && (
            <>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-10 h-10 text-yellow-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Taking Longer Than Expected
              </h2>
              <p className="text-slate-600 mb-6">
                Your payment is being processed. Please check your email for confirmation.
              </p>
              <Button 
                onClick={() => navigate("/dashboard")}
                className="bg-maple hover:bg-maple-600 text-white w-full h-12"
              >
                Go to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
